import React, { useEffect, useCallback } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faAt,
  faMapMarkerAlt,
  faCode,
  faLanguage,
  faCertificate,
  faStar, // Keep faStar for proficiency
} from '@fortawesome/free-solid-svg-icons';
import {
  faGithub,
  faLinkedin,
} from '@fortawesome/free-brands-svg-icons';
import { makeStyles } from '@mui/styles';
import { BaseLayout } from '../BaseLayout';
import { useLanguage } from '../../localization';
// import { renderProficiencyVisualization } from '../../styles/proficiency/ProficiencyRenderer';

// Generic layout component with a sidebar on the left or right
const SidebarLayout = ({
  formData,
  selectedSections,
  design,
  typographySettings,
  typographyStyles,
  selectedStyle,
  activeStyle,
  selectedBackground,
  activeBackgroundComponent, // Added prop for background component
  selectedSpecialEffectId,
  activeBarStyle, // Added activeBarStyle
  currentPage = 0,
  totalPages = 1,
  measureMode = false,
  isPdfExport = false,
  onRenderComplete,
  sidebarPosition = 'left', // 'left' or 'right'
}) => {
  const { t } = useLanguage();
  
  // Call onRenderComplete once, after first mount
  useEffect(() => {
    if (typeof onRenderComplete === 'function') {
      onRenderComplete();
    }
  }, [onRenderComplete]);

  // Define sections that should go in sidebar
  const sidebarSectionNames = ['Skills', 'Languages', 'Certifications'];

  // --- Determine effective colors directly from design object ---
  const effectiveMainBgColor = design?.colors?.background || '#ffffff'; // Default white
  const effectiveSidebarBgColor = design?.colors?.sidebarBackground || '#f5f5f5'; // Default light gray

  // --- Background is now handled centrally by LayoutResolver --- 
  // All layouts should use transparent background to let centrally-rendered backgrounds show through
  const layoutBackgroundColor = selectedBackground === 'blank' 
                                ? effectiveMainBgColor 
                                : 'transparent';

  // Create styles dynamically based on sidebarPosition
  const useStyles = makeStyles({
    container: {
      width: '210mm',
      minHeight: '297mm',
      padding: 0,
      boxSizing: 'border-box',
      color: design?.colors?.text || '#000000',
      display: 'flex',
      flexDirection: sidebarPosition === 'right' ? 'row-reverse' : 'row',
      ...typographyStyles,
      "& *": {
        wordWrap: "break-word",
        overflowWrap: "break-word",
      }
    },
    sidebar: {
      backgroundColor: effectiveSidebarBgColor,
      width: '70mm',
      minHeight: '297mm',
      padding: '15mm 10mm',
      boxSizing: 'border-box',
      borderRight: sidebarPosition === 'left' ? `1px solid ${design?.colors?.border || '#ddd'}` : 'none',
      borderLeft: sidebarPosition === 'right' ? `1px solid ${design?.colors?.border || '#ddd'}` : 'none',
      flexShrink: 0,
      position: 'relative',
      zIndex: 1,
      color: design?.colors?.sidebarText || design?.colors?.text || '#000000',
    },
    mainContent: {
      width: '140mm',
      minHeight: '297mm',
      padding: sidebarPosition === 'left' ? '15mm 10mm 15mm 15mm' : '15mm 15mm 15mm 10mm',
      boxSizing: 'border-box',
      flexGrow: 1,
      position: 'relative',
      zIndex: 1,
      backgroundColor: 'transparent',
    },
    profilePhoto: {
      width: '100px',
      height: '100px',
      margin: '0 auto 15px',
      border: `3px solid ${design?.colors?.sidebarPrimary || design?.colors?.primary || '#000000'}`,
    },
    headerName: {
      textAlign: 'center',
    },
    headerTitle: {
      marginTop: '5px',
      textAlign: 'center',
    },
    contactSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginTop: '20px',
    },
    contactItem: {
      color: design?.colors?.sidebarText || design?.colors?.text || '#000000',
      '&': BaseLayout.getTypographyStyles(typographySettings)['& .contactItem'],
    },
    contactIcon: {
      color: design?.colors?.sidebarPrimary || design?.colors?.primary || '#000000',
      fontSize: '16px',
      width: '16px',
      minWidth: '16px',
      marginRight: '8px',
      verticalAlign: 'middle',
    },
    sidebarSection: {
      marginTop: '25px',
    },
    sidebarSectionTitle: {
      color: design?.colors?.sidebarText || design?.colors?.primary || '#000000',
      borderBottom: `2px solid transparent`,
      paddingBottom: '5px',
      marginBottom: '10px',
      display: 'flex',
      alignItems: 'center',
      '&': BaseLayout.getTypographyStyles(typographySettings)['& .sidebarSectionTitle'],
    },
    sidebarItem: {
      marginBottom: '8px',
    },
    sidebarItemTitle: {
      color: design?.colors?.sidebarText || design?.colors?.text || '#000000',
      '&': BaseLayout.getTypographyStyles(typographySettings)['& .sidebarItemTitle'],
    },
    sidebarItemSubtitle: {
      color: design?.colors?.sidebarText || design?.colors?.textSecondary || '#666666',
      '&': BaseLayout.getTypographyStyles(typographySettings)['& .sidebarItemSubtitle'],
    },
    section: {
      marginBottom: '20px',
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'baseline',
      marginBottom: '10px',
      paddingBottom: '5px',
      borderBottomWidth: '2px',
      borderBottomStyle: 'solid',
    },
    sectionIcon: {
      marginRight: '8px',
      color: design?.colors?.primary || '#000000',
      verticalAlign: 'middle',
    },
    sectionTitle: {
      color: design?.colors?.primary || '#000000',
      '&': BaseLayout.getTypographyStyles(typographySettings)['& .sectionTitle'],
    },
    sectionContent: {
    },
    sectionItem: {
      marginBottom: '16px',
    },
    itemTitle: {
      color: design?.colors?.text || '#000000',
      '&': BaseLayout.getTypographyStyles(typographySettings)['& .itemTitle'],
    },
    itemSubtitle: {
      color: design?.colors?.textSecondary || '#666666',
      marginTop: '2px',
      '&': BaseLayout.getTypographyStyles(typographySettings)['& .itemSubtitle'],
    },
    itemDate: {
      color: design?.colors?.textSecondary || '#666666',
      '&': BaseLayout.getTypographyStyles(typographySettings)['& .itemDate'],
    },
    itemDescription: {
      color: design?.colors?.text || '#000000',
      marginTop: '4px',
      '&': BaseLayout.getTypographyStyles(typographySettings)['& .itemDescription'],
    },
    richText: {
      marginTop: '8px',
      '&': BaseLayout.getTypographyStyles(typographySettings)['& .richText'],
      '& p': BaseLayout.getTypographyStyles(typographySettings)['& .richText p'],
      '& ul': BaseLayout.getTypographyStyles(typographySettings)['& .richText ul'],
      '& ol': BaseLayout.getTypographyStyles(typographySettings)['& .richText ol'],
      '& li': BaseLayout.getTypographyStyles(typographySettings)['& .richText li'],
    },
    itemLink: {
      color: design?.colors?.sidebarPrimary || design?.colors?.sidebarText || design?.colors?.primary || '#0000FF',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
      '.contactItem &': {
         display: 'inline',
         marginLeft: '5px',
      }
    },
    paragraph: {
      '&': BaseLayout.getTypographyStyles(typographySettings)['& .paragraph'],
    },
    skillCategory: {
      marginBottom: '10px',
    },
    proficiencyVisualization: {
    },
    proficiencyFilled: {
    },
    proficiencyEmpty: {
    },
    smallText: {
      color: design?.colors?.textSecondary || '#666666',
      '&': BaseLayout.getTypographyStyles(typographySettings)['& .smallText'],
    },
  });

  const classes = useStyles();

  // Get personal info from form data
  const { personalInfo = {} } = formData;

  // --- DEBUG LOG ---
  // console.log('SidebarLayout - selectedBackground:', selectedBackground);
  // console.log('SidebarLayout - design colors:', design?.colors);
  // console.log('SidebarLayout - typographyStyles:', typographyStyles); // Log received styles
  // --- END DEBUG LOG ---

  // Filter sections for main content
  const mainSections = selectedSections.filter(
    section => !sidebarSectionNames.includes(section.name) || currentPage > 0
  );

  // --- Sidebar Section Renderers ---
  const renderSkillsForSidebar = () => {
    const skillsData = formData?.skills;
    if (!skillsData || skillsData.length === 0) return null;
    const sectionIconColor = design?.colors?.sidebarPrimary || design?.colors?.primary || '#000000';
    const ActiveProficiencyComponent = activeBarStyle?.component;

    // NEW: Define proficiency colors for sidebar context
    const sidebarProficiencyColors = {
      ...(design?.colors || {}),
      primary: design?.colors?.sidebarPrimary || design?.colors?.primary,
      // If you want to customize other colors for the bar (e.g., background/empty part):
      // quaternary: design?.colors?.sidebarText || design?.colors?.quaternary, // Example for empty part
    };

    return (
      <Box className={classes.sidebarSection} data-section="Skills">
        <Typography variant="h4" className="sidebarSectionTitle">
          <FontAwesomeIcon icon={faCode} style={{ marginRight: '8px', color: sectionIconColor }} />
          {t ? t('inputSection.sections.Skills') : 'Skills'}
        </Typography>
        {skillsData.map((item, index) => (
          item.displayFormat === 'individual' && (
            <Box key={item.id || index} className={classes.sidebarItem}>
              <Typography className="sidebarItemTitle" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {item.skillName}
                {ActiveProficiencyComponent && item.proficiency &&
                  <ActiveProficiencyComponent proficiency={item.proficiency} classes={classes} colors={sidebarProficiencyColors} />
                }
              </Typography>
            </Box>
          )
        ))}
      </Box>
    );
  };

  const renderLanguagesForSidebar = () => {
    const languagesData = formData?.languages;
    if (!languagesData || languagesData.length === 0) return null;
    const sectionIconColor = design?.colors?.sidebarPrimary || design?.colors?.primary || '#000000';
    const ActiveProficiencyComponent = activeBarStyle?.component;

    // NEW: Define proficiency colors for sidebar context (can be reused or redefined if needed)
    const sidebarProficiencyColors = {
      ...(design?.colors || {}),
      primary: design?.colors?.sidebarPrimary || design?.colors?.primary,
      // quaternary: design?.colors?.sidebarText || design?.colors?.quaternary, // Example for empty part
    };

    return (
      <Box className={classes.sidebarSection} data-section="Languages">
        <Typography variant="h4" className="sidebarSectionTitle">
          <FontAwesomeIcon icon={faLanguage} style={{ marginRight: '8px', color: sectionIconColor }} />
          {t ? t('inputSection.sections.Languages') : 'Languages'}
        </Typography>
        {languagesData.map((item, index) => (
          <Box key={item.id || index} className={classes.sidebarItem}>
            <Typography className="sidebarItemTitle" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {item.language}
              {ActiveProficiencyComponent && item.proficiency &&
                <ActiveProficiencyComponent proficiency={item.proficiency} classes={classes} colors={sidebarProficiencyColors} />
              }
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderCertificationsForSidebar = () => {
    if (!formData.certifications || formData.certifications.length === 0) return null;

    return (
      <Box className={classes.sidebarSection} data-section="Certifications">
        <Typography className="sidebarSectionTitle">
          {activeStyle?.settings?.useContactIcons && (
            <FontAwesomeIcon icon={faCertificate} className={classes.contactIcon} />
          )}
          {t ? t('inputSection.sections.Certifications') : 'Certifications'}
        </Typography>
        {formData.certifications.map((item) => (
          <Box key={item.id || item.name || Math.random()} className={classes.skillCategory}>
            {item.credentialUrl ? (
              <a href={item.credentialUrl} className={classes.itemLink} target="_blank" rel="noopener noreferrer">
                <Typography component="span" className="sidebarItemTitle" sx={{ display: 'inline' }}>
                  {item.name || item.certificationName}
                </Typography>
              </a>
            ) : (
              <Typography className="sidebarItemTitle">
                {item.name || item.certificationName}
              </Typography>
            )}
            <Typography className="sidebarItemSubtitle">
              {item.issuer || item.issuingOrganization}
              {(item.date || item.issueDate) && ` | ${BaseLayout.formatDate(item.date || item.issueDate)}`}
              {item.credentialId && ` | ID: ${item.credentialId}`}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };
  // --- End Sidebar Section Renderers ---


  // --- Common JSX Structure ---

  // Memoize SidebarContent to prevent unnecessary re-renders
  const SidebarContent = useCallback(() => (
    <Box className={`${classes.sidebar} sidebar`}>
      {/* Profile Photo */}
      {(() => {
        // Don't show photo space if enablePictureSpace is false
        if (personalInfo.enablePictureSpace === false) {
          return null;
        }
        
        // Show actual photo if it exists
        if (personalInfo.photo) {
          return (
            <Avatar
              src={personalInfo.photo}
              alt={`${personalInfo.firstName} ${personalInfo.lastName}`}
              className={classes.profilePhoto}
              sx={{ width: '100px', height: '100px' }}
            />
          );
        }
        
        // Show invisible placeholder when enablePictureSpace is true but no photo
        return (
          <Box
            sx={{
              width: '100px',
              height: '100px',
              margin: '0 auto 15px', // Match profilePhoto class margin
              visibility: 'hidden', // Invisible but takes up space
            }}
          />
        );
      })()}

      {/* Name */}
      {personalInfo.firstName && personalInfo.lastName && (
        <Typography 
          className="headerName"
          sx={{ color: `${design?.colors?.sidebarText || design?.colors?.primary || '#000000'} !important` }}
        >
          {BaseLayout.safeRender(personalInfo.firstName)} {BaseLayout.safeRender(personalInfo.lastName)}
        </Typography>
      )}

      {/* Professional Title */}
      {personalInfo.professionalTitle && (
        <Typography 
          className="headerTitle"
          sx={{ color: `${design?.colors?.sidebarText || design?.colors?.secondary || '#333333'} !important` }}
        >
          {BaseLayout.safeRender(personalInfo.professionalTitle)}
        </Typography>
      )}

      {/* Contact Information */}
      <Box className={classes.contactSection}>
        {personalInfo.phone && (
          <Box className="contactItem">
            {activeStyle?.settings?.useContactIcons && (
              <FontAwesomeIcon icon={faPhone} className={classes.contactIcon} />
            )}
            {BaseLayout.safeRender(personalInfo.phone)}
          </Box>
        )}
        {personalInfo.email && (
          <Box className="contactItem">
            {activeStyle?.settings?.useContactIcons && (
              <FontAwesomeIcon icon={faAt} className={classes.contactIcon} />
            )}
            {BaseLayout.safeRender(personalInfo.email)}
          </Box>
        )}
        {personalInfo.linkedin && (
          <Box className="contactItem">
            {activeStyle?.settings?.useContactIcons && (
              <FontAwesomeIcon icon={faLinkedin} className={classes.contactIcon} />
            )}
            <a href={personalInfo.linkedin} className={classes.itemLink} target="_blank" rel="noopener noreferrer">
               {BaseLayout.safeRender(personalInfo.linkedin)}
            </a>
          </Box>
        )}
        {personalInfo.github && (
          <Box className="contactItem">
            {activeStyle?.settings?.useContactIcons && (
              <FontAwesomeIcon icon={faGithub} className={classes.contactIcon} />
            )}
             <a href={personalInfo.github} className={classes.itemLink} target="_blank" rel="noopener noreferrer">
                {BaseLayout.safeRender(personalInfo.github)}
             </a>
          </Box>
        )}
        {personalInfo.address && (
          <Box className="contactItem">
            {activeStyle?.settings?.useContactIcons && (
              <FontAwesomeIcon icon={faMapMarkerAlt} className={classes.contactIcon} />
            )}
            {BaseLayout.safeRender(personalInfo.address)}
          </Box>
        )}
      </Box>

      {/* Sidebar Sections with custom renderers */}
      {formData.skills && formData.skills.length > 0 && renderSkillsForSidebar()}
      {formData.languages && formData.languages.length > 0 && renderLanguagesForSidebar()}
      {formData.certifications && formData.certifications.length > 0 && renderCertificationsForSidebar()}
    </Box>
  ), [
      classes,
      personalInfo, // Derived from formData
      selectedStyle,
      activeStyle,
      formData, // Needed for the section renderers inside
      design, // Needed for proficiency viz colors
      // renderSkillsForSidebar, renderLanguagesForSidebar, renderCertificationsForSidebar could be dependencies if defined outside useCallback scope, but they are defined inside SidebarLayout scope and depend on formData/classes/design/selectedStyle already listed.
  ]); // Add dependencies used inside SidebarContent


  // Memoize MainContent to prevent unnecessary re-renders, especially of TechBackground
  const MainContent = useCallback(() => (
    <Box
      className={classes.mainContent}
      sx={{
        width: currentPage === 0 ? '140mm' : '210mm',
        padding: currentPage === 0
          ? (sidebarPosition === 'left' ? '15mm 10mm 15mm 15mm' : '15mm 15mm 15mm 10mm')
          : '15mm',
      }}
    >
      {/* Render sections */}
      {mainSections.map((section) => {
        if (currentPage === 0 && sidebarSectionNames.includes(section.name)) {
          return null;
        }
        switch(section.name) {
          case 'Professional Summary':
            return BaseLayout.renderProfessionalSummary(formData, classes, design?.colors, activeStyle, 'sidebar', selectedBackground, selectedSpecialEffectId, t);
          case 'Work Experience':
            return BaseLayout.renderWorkExperiences(formData, classes, design?.colors, activeStyle, 'sidebar', selectedBackground, selectedSpecialEffectId, t);
          case 'Education':
            return BaseLayout.renderEducations(formData, classes, design?.colors, activeStyle, 'sidebar', selectedBackground, selectedSpecialEffectId, t);
          case 'Skills':
            return BaseLayout.renderSkills(formData, classes, design?.colors, activeStyle, 'sidebar', selectedBackground, selectedSpecialEffectId, activeBarStyle, t);
          case 'Projects':
            return BaseLayout.renderProjects(formData, classes, design?.colors, activeStyle, 'sidebar', selectedBackground, selectedSpecialEffectId, t);
          case 'Certifications':
            return BaseLayout.renderCertifications(formData, classes, design?.colors, activeStyle, 'sidebar', selectedBackground, selectedSpecialEffectId, t);
          case 'Awards and Honors':
            return BaseLayout.renderAwardsAndHonors(formData, classes, design?.colors, activeStyle, 'sidebar', selectedBackground, selectedSpecialEffectId, t);
          case 'Languages':
            return BaseLayout.renderLanguages(formData, classes, design?.colors, activeStyle, 'sidebar', selectedBackground, selectedSpecialEffectId, activeBarStyle, t);
          case 'Volunteer Experience':
            return BaseLayout.renderVolunteerExperiences(formData, classes, design?.colors, activeStyle, 'sidebar', selectedBackground, selectedSpecialEffectId, t);
          case 'Publications':
            return BaseLayout.renderPublications(formData, classes, design?.colors, activeStyle, 'sidebar', selectedBackground, selectedSpecialEffectId, t);
          case 'References':
            return BaseLayout.renderReferences(formData, classes, design?.colors, activeStyle, 'sidebar', selectedBackground, selectedSpecialEffectId, t);
          default:
             console.warn(`Render function not explicitly mapped for section: ${section.name}`);
            return null;
        }
      })}
    </Box>
  ), [
      classes,
      currentPage,
      sidebarPosition,
      design, // Still needed for section renderers
      mainSections, // Derived from selectedSections
      formData, // Prop for section renderers
      activeStyle, // Added dependency
      selectedBackground, // Pass selectedBackground down
      selectedSpecialEffectId, // Added dependency
      sidebarSectionNames, // Used in map condition
  ]); // Add dependencies used inside MainContent

  console.log('SidebarLayout - selectedBackground:', selectedBackground, 'layoutBackgroundColor:', layoutBackgroundColor);

  return (
    <Box
      className={classes.container}
      sx={{
        position: 'relative',
        display: currentPage === 0 ? 'flex' : 'block',
        flexDirection: currentPage === 0 ? (sidebarPosition === 'right' ? 'row-reverse' : 'row') : undefined,
        backgroundColor: layoutBackgroundColor,
        ...(measureMode ? { position: 'absolute', visibility: 'hidden' } : {}),
        ...(isPdfExport ? { position: 'relative', visibility: 'visible' } : {}),
      }}
    >
      {/* Render Sidebar only on the first page */}
      {currentPage === 0 && <SidebarContent />}

      {/* Render Main Content (now dynamically sized and contains its own background) */}
      <MainContent />

      {/* Page number - Positioned relative to the main container */}
      {totalPages > 1 && !measureMode && (
        <Typography
          className="smallText"
          sx={{
            position: 'absolute',
            bottom: '10mm',
            right: '10mm',
            color: design?.colors?.textSecondary || '#666',
            zIndex: 2,
          }}
        >
          Page {currentPage + 1} of {totalPages}
        </Typography>
      )}
    </Box>
  );
};

export default SidebarLayout; 